import React, { useState, useEffect } from 'react';
import { Calendar, FileText, Eye, RefreshCw, Download, Archive, CheckCircle, XCircle, Target, Brain } from 'lucide-react';

const PDIMultiFonteHistorico = ({ colaboradorId, colaboradorNome }) => {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPDI, setSelectedPDI] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (colaboradorId) {
      carregarHistorico();
    }
  }, [colaboradorId]);

  const carregarHistorico = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdi/multisource/historico/${colaboradorId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setHistorico(data.historico || []);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoading(false);
    }
  };

  const verDetalhes = async (pdiId) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdi/multisource/historico/${colaboradorId}/${pdiId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSelectedPDI(data.pdi);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Erro ao carregar PDI:', error);
      alert('Erro ao carregar detalhes do PDI');
    } finally {
      setLoading(false);
    }
  };

  const regenerarPDI = async (pdiId) => {
    if (!window.confirm('Deseja regenerar este PDI com dados atualizados? O PDI atual será arquivado.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdi/multisource/regenerar/${pdiId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        alert('PDI regenerado com sucesso!');
        carregarHistorico();
      } else {
        alert('Erro ao regenerar PDI');
      }
    } catch (error) {
      console.error('Erro ao regenerar PDI:', error);
      alert('Erro ao regenerar PDI');
    } finally {
      setLoading(false);
    }
  };

  const exportarPDF = (pdi) => {
    // Gerar HTML para impressão
    const htmlContent = gerarHTMLParaPDF(pdi);
    
    // Abrir em nova janela para impressão
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const gerarHTMLParaPDF = (pdi) => {
    const pdiData = pdi.pdi_gerado || {};
    const acoes = pdiData.acoes || [];
    const temas = pdiData.temas_prioritarios || [];

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>PDI Multi-fonte - ${pdi.employee_nome}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      color: #333;
    }
    h1 {
      color: #2563eb;
      border-bottom: 3px solid #2563eb;
      padding-bottom: 10px;
    }
    h2 {
      color: #1e40af;
      margin-top: 30px;
    }
    .info-box {
      background: #f3f4f6;
      padding: 15px;
      border-radius: 8px;
      margin: 20px 0;
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      margin: 5px 0;
    }
    .acao {
      border: 1px solid #e5e7eb;
      padding: 15px;
      margin: 15px 0;
      border-radius: 8px;
      page-break-inside: avoid;
    }
    .acao-titulo {
      font-weight: bold;
      font-size: 16px;
      color: #1e40af;
      margin-bottom: 8px;
    }
    .badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      margin-right: 8px;
    }
    .badge-critica { background: #fecaca; color: #991b1b; }
    .badge-alta { background: #fed7aa; color: #9a3412; }
    .badge-media { background: #fef08a; color: #854d0e; }
    .tema {
      background: #dbeafe;
      color: #1e40af;
      padding: 8px 16px;
      border-radius: 16px;
      display: inline-block;
      margin: 4px;
    }
    @media print {
      body { margin: 0; }
    }
  </style>
</head>
<body>
  <h1>📊 PDI Multi-fonte Inteligente</h1>
  
  <div class="info-box">
    <div class="info-row">
      <strong>Colaborador:</strong>
      <span>${pdi.employee_nome}</span>
    </div>
    <div class="info-row">
      <strong>Período Analisado:</strong>
      <span>${formatDate(pdi.periodo_inicio)} até ${formatDate(pdi.periodo_fim)}</span>
    </div>
    <div class="info-row">
      <strong>Fontes Utilizadas:</strong>
      <span>${pdi.total_fontes} fontes</span>
    </div>
    <div class="info-row">
      <strong>Data de Geração:</strong>
      <span>${formatDate(pdi.gerado_em)}</span>
    </div>
  </div>

  <h2>🎯 Temas Prioritários</h2>
  <div>
    ${temas.map(tema => `<span class="tema">${tema}</span>`).join('')}
  </div>

  <h2>📋 Ações de Desenvolvimento</h2>
  ${acoes.map((acao, index) => `
    <div class="acao">
      <div class="acao-titulo">${index + 1}. ${acao.titulo}</div>
      <div style="margin: 8px 0;">
        <span class="badge badge-${acao.prioridade}">${acao.prioridade?.toUpperCase()}</span>
        <span class="badge" style="background: #e0e7ff; color: #3730a3;">${acao.tipo}</span>
      </div>
      <p><strong>Objetivo:</strong> ${acao.objetivo}</p>
      <p><strong>Justificativa:</strong> ${acao.justificativa}</p>
      <p><strong>Prazo:</strong> ${acao.prazo_dias} dias</p>
      <p><strong>Impacto Esperado:</strong> ${acao.impacto_esperado}</p>
      ${acao.fontes ? `<p><strong>Fontes:</strong> ${acao.fontes.join(', ')}</p>` : ''}
    </div>
  `).join('')}

  ${pdiData.observacoes_gerais ? `
    <h2>💡 Observações Gerais</h2>
    <div class="info-box">
      <p>${pdiData.observacoes_gerais}</p>
    </div>
  ` : ''}

  <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
    <p>Documento gerado automaticamente pelo Sistema GridProfile HR</p>
    <p>${new Date().toLocaleString('pt-BR')}</p>
  </div>
</body>
</html>
    `;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    if (status === 'ativo') {
      return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">Ativo</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">Arquivado</span>;
  };

  const getPrioridadeBadge = (prioridade) => {
    const cores = {
      'critica': 'bg-red-100 text-red-800',
      'alta': 'bg-orange-100 text-orange-800',
      'media': 'bg-yellow-100 text-yellow-800',
      'baixa': 'bg-green-100 text-green-800'
    };
    return `px-2 py-1 ${cores[prioridade] || 'bg-gray-100 text-gray-800'} text-xs font-semibold rounded-full`;
  };

  if (!colaboradorId) {
    return (
      <div className="text-center py-8 text-gray-500">
        Selecione um colaborador para ver o histórico de PDIs
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Histórico de PDIs</h2>
          <p className="text-gray-600">{colaboradorNome}</p>
        </div>
        <button
          onClick={carregarHistorico}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Lista de PDIs */}
      {loading && historico.length === 0 ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando histórico...</p>
        </div>
      ) : historico.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nenhum PDI foi gerado ainda</p>
          <p className="text-sm text-gray-500 mt-2">Gere um novo PDI na aba principal</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {historico.map((pdi) => (
            <div
              key={pdi.id}
              className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      {formatDate(pdi.periodo_inicio)} - {formatDate(pdi.periodo_fim)}
                    </h3>
                    {getStatusBadge(pdi.status)}
                  </div>

                  <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                    <div>
                      <p className="text-gray-600">Total de Fontes</p>
                      <p className="font-semibold text-gray-800">{pdi.total_fontes}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Ações Geradas</p>
                      <p className="font-semibold text-gray-800">{pdi.total_acoes}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Temas Prioritários</p>
                      <p className="font-semibold text-gray-800">{pdi.total_temas}</p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    Gerado em: {formatDate(pdi.gerado_em)}
                  </p>
                </div>

                <div className="flex flex-col gap-2 ml-4">
                  <button
                    onClick={() => verDetalhes(pdi.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Eye className="h-4 w-4" />
                    Ver Detalhes
                  </button>

                  {pdi.status === 'ativo' && (
                    <button
                      onClick={() => regenerarPDI(pdi.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Regenerar
                    </button>
                  )}

                  <button
                    onClick={() => verDetalhes(pdi.id).then(() => exportarPDF(selectedPDI))}
                    className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Exportar PDF
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Detalhes */}
      {showModal && selectedPDI && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">Detalhes do PDI</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => exportarPDF(selectedPDI)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Download className="h-4 w-4" />
                  Exportar PDF
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Fechar
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Informações Gerais */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-3">Informações Gerais</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Colaborador</p>
                    <p className="font-semibold">{selectedPDI.employee_nome}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Período</p>
                    <p className="font-semibold">
                      {formatDate(selectedPDI.periodo_inicio)} - {formatDate(selectedPDI.periodo_fim)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Fontes Utilizadas</p>
                    <p className="font-semibold">{selectedPDI.total_fontes}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Data de Geração</p>
                    <p className="font-semibold">{formatDate(selectedPDI.gerado_em)}</p>
                  </div>
                </div>
              </div>

              {/* Temas Prioritários */}
              {selectedPDI.pdi_gerado?.temas_prioritarios && selectedPDI.pdi_gerado.temas_prioritarios.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    Temas Prioritários
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedPDI.pdi_gerado.temas_prioritarios.map((tema, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {tema}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Ações de Desenvolvimento */}
              {selectedPDI.pdi_gerado?.acoes && selectedPDI.pdi_gerado.acoes.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Ações de Desenvolvimento ({selectedPDI.pdi_gerado.acoes.length})
                  </h4>
                  <div className="space-y-4">
                    {selectedPDI.pdi_gerado.acoes.map((acao, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <h5 className="font-semibold text-gray-800">{index + 1}. {acao.titulo}</h5>
                          <div className="flex gap-2">
                            <span className={getPrioridadeBadge(acao.prioridade)}>
                              {acao.prioridade?.toUpperCase()}
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                              {acao.tipo}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div>
                            <p className="text-gray-600 font-medium">Objetivo:</p>
                            <p className="text-gray-800">{acao.objetivo}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 font-medium">Justificativa:</p>
                            <p className="text-gray-800">{acao.justificativa}</p>
                          </div>
                          <div className="flex gap-4">
                            <div>
                              <p className="text-gray-600 font-medium">Prazo:</p>
                              <p className="text-gray-800">{acao.prazo_dias} dias</p>
                            </div>
                            {acao.fontes && acao.fontes.length > 0 && (
                              <div>
                                <p className="text-gray-600 font-medium">Fontes:</p>
                                <p className="text-gray-800">{acao.fontes.join(', ')}</p>
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-600 font-medium">Impacto Esperado:</p>
                            <p className="text-gray-800">{acao.impacto_esperado}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Observações Gerais */}
              {selectedPDI.pdi_gerado?.observacoes_gerais && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <Brain className="h-5 w-5 text-purple-600" />
                    Observações da IA
                  </h4>
                  <p className="text-gray-700 text-sm">{selectedPDI.pdi_gerado.observacoes_gerais}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDIMultiFonteHistorico;
